
import React, { useState, useEffect, useRef } from 'react';
import { useCRMLeads } from '@/hooks/useCRMLeads';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  TrendingUp,
  Plus,
  RefreshCw,
  Users,
  Target,
  CheckCircle,
  XCircle,
  CheckSquare,
  Square,
  Trash2,
  UserPlus
} from 'lucide-react';
import LeadCard from '@/components/crm/LeadCard';
import { QuickscanLeadCard } from '@/components/crm/QuickscanLeadCard';
import LeadDetailModal from '@/components/crm/LeadDetailModal';
import NewLeadModal from '@/components/crm/NewLeadModal';
import { BulkActionsToolbar } from '@/components/crm/BulkActionsToolbar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { useIsMobile } from '@/hooks/use-mobile';

const LEAD_STAGES = [
  { value: 'nieuw', label: 'Nieuw', color: 'bg-blue-500' },
  { value: 'contact_opgenomen', label: 'Contact Opgenomen', color: 'bg-yellow-500' },
  { value: 'gekwalificeerd', label: 'Gekwalificeerd', color: 'bg-orange-500' },
  { value: 'voorstel', label: 'Voorstel', color: 'bg-purple-500' },
  { value: 'onderhandeling', label: 'Onderhandeling', color: 'bg-indigo-500' },
  { value: 'gewonnen', label: 'Gewonnen', color: 'bg-green-500' },
  { value: 'verloren', label: 'Verloren', color: 'bg-red-500' }
];

const CRMLeads = () => {
  const isMobile = useIsMobile();
  const { leads, loading, refetch, updateLeadStage, bulkUpdateStage, bulkUpdateOwner, bulkDelete } = useCRMLeads();
  const [selectedLead, setSelectedLead] = useState<string | null>(null);
  const [showNewLeadModal, setShowNewLeadModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedLeads, setSelectedLeads] = useState<Set<string>>(new Set());

  // Scroll shadow state
  const scrollRef = useRef<HTMLDivElement>(null);
  const [showLeftShadow, setShowLeftShadow] = useState(false);
  const [showRightShadow, setShowRightShadow] = useState(true);

  // Check scroll position for shadows
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollLeft, scrollWidth, clientWidth } = e.currentTarget;
    setShowLeftShadow(scrollLeft > 20);
    setShowRightShadow(scrollLeft < scrollWidth - clientWidth - 20);
  };

  const filteredLeads = leads.filter(lead => {
    const displayName = lead.organization?.name || 
                       lead.quickscan_data?.company_name || 
                       (lead.contact ? `${lead.contact.first_name || ''} ${lead.contact.last_name || ''}`.trim() : '') ||
                       'Onbekende lead';
    const displayEmail = lead.contact?.email || lead.quickscan_data?.email || '';
    const ownerName = lead.owner?.name || lead.owner?.email || '';
    
    return displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
           displayEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
           ownerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
           lead.summary?.toLowerCase().includes(searchTerm.toLowerCase()) ||
           lead.source.toLowerCase().includes(searchTerm.toLowerCase()) ||
           (lead.quickscan_data?.business_type || '').toLowerCase().includes(searchTerm.toLowerCase());
  });

  // Initial shadow check (after filteredLeads is defined)
  useEffect(() => {
    if (scrollRef.current) {
      const { scrollWidth, clientWidth } = scrollRef.current;
      setShowRightShadow(scrollWidth > clientWidth);
    }
  }, [filteredLeads]);

  const getLeadsByStage = (stage: string) => {
    return filteredLeads.filter(lead => lead.stage === stage);
  };

  const handleDragStart = (e: React.DragEvent, leadId: string) => {
    e.dataTransfer.setData('text/plain', leadId);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleDrop = async (e: React.DragEvent, newStage: string) => {
    e.preventDefault();
    const leadId = e.dataTransfer.getData('text/plain');
    await updateLeadStage(leadId, newStage as any);
  };

  const selectedLeadData = selectedLead ? leads.find(l => l.id === selectedLead) : null;

  // Bulk selection handlers
  const toggleSelection = (leadId: string) => {
    const newSelected = new Set(selectedLeads);
    if (newSelected.has(leadId)) {
      newSelected.delete(leadId);
    } else {
      newSelected.add(leadId);
    }
    setSelectedLeads(newSelected);
  };

  const selectAll = () => {
    setSelectedLeads(new Set(filteredLeads.map(lead => lead.id)));
  };

  const clearSelection = () => {
    setSelectedLeads(new Set());
    setSelectionMode(false);
  };

  const handleBulkStageUpdate = async (newStage: string) => {
    const leadIds = Array.from(selectedLeads);
    await bulkUpdateStage(leadIds, newStage as any);
    clearSelection();
  };

  const handleBulkOwnerUpdate = async (ownerId: string) => {
    const leadIds = Array.from(selectedLeads);
    await bulkUpdateOwner(leadIds, ownerId);
    clearSelection();
  };

  const handleBulkDelete = async () => {
    const leadIds = Array.from(selectedLeads);
    const success = await bulkDelete(leadIds);
    if (success) {
      clearSelection();
    }
  };

  if (loading) {
    return (
      <div className="text-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
        <p className="mt-2 text-gray-600">Laden...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Header - responsive */}
      <div className="flex flex-col gap-4">
        <div>
          <h2 className="text-xl sm:text-2xl font-bold text-foreground">Lead Management</h2>
          <p className="text-sm text-muted-foreground">Beheer leads uit verschillende bronnen</p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full">
          <Input
            placeholder="Zoek leads..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full sm:w-64"
          />
          <div className="grid grid-cols-2 sm:flex gap-2">
            <Button 
              variant={selectionMode ? "default" : "outline"} 
              size="sm"
              onClick={() => {
                setSelectionMode(!selectionMode);
                if (selectionMode) clearSelection();
              }}
              className="sm:size-default"
            >
              {selectionMode ? <CheckSquare className="h-4 w-4 sm:mr-2" /> : <Square className="h-4 w-4 sm:mr-2" />}
              <span className="hidden sm:inline">{selectionMode ? 'Selectie modus' : 'Selecteren'}</span>
            </Button>
            <Button variant="outline" size="sm" onClick={refetch} className="sm:size-default">
              <RefreshCw className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Vernieuwen</span>
            </Button>
            <Button onClick={() => setShowNewLeadModal(true)} size="sm" className="col-span-2 sm:col-span-1 sm:size-default">
              <Plus className="h-4 w-4 sm:mr-2" />
              <span className="sm:hidden">Nieuw</span>
              <span className="hidden sm:inline">Nieuwe Lead</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Stats Cards - responsive grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Totaal Leads</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{leads.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Actieve Leads</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {leads.filter(l => !['gewonnen', 'verloren'].includes(l.stage)).length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Gewonnen</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {leads.filter(l => l.stage === 'gewonnen').length}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Conversion Rate</CardTitle>
            <Target className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {leads.length > 0 
                ? Math.round((leads.filter(l => l.stage === 'gewonnen').length / leads.length) * 100)
                : 0}%
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Kanban Board with scroll shadows */}
      <div className="relative -mx-4 sm:-mx-6">
        {/* Left scroll shadow */}
        <div 
          className={`absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-background to-transparent pointer-events-none z-10 transition-opacity duration-200 ${showLeftShadow ? 'opacity-100' : 'opacity-0'}`} 
        />
        {/* Right scroll shadow */}
        <div 
          className={`absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-background to-transparent pointer-events-none z-10 transition-opacity duration-200 ${showRightShadow ? 'opacity-100' : 'opacity-0'}`} 
        />
        
        <div 
          ref={scrollRef}
          className="flex gap-4 sm:gap-6 overflow-x-auto pb-4 px-4 sm:px-6 kanban-scroll touch-pan-x"
          style={{ WebkitOverflowScrolling: 'touch' }}
          onScroll={handleScroll}
        >
          {LEAD_STAGES.map(stage => {
            const stageLeads = getLeadsByStage(stage.value);
            return (
              <div
                key={stage.value}
                className="bg-muted/50 rounded-lg p-3 sm:p-5 min-h-[500px] sm:min-h-[600px] min-w-[280px] sm:min-w-[420px] flex-shrink-0 shadow-sm border"
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(e, stage.value)}
              >
                <div className="sticky top-0 z-10 bg-muted/95 backdrop-blur-sm -mx-3 sm:-mx-5 -mt-3 sm:-mt-5 px-3 sm:px-5 pt-3 sm:pt-5 pb-3 sm:pb-4 mb-3 sm:mb-4 border-b">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 sm:gap-3">
                      <div className={`w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full ${stage.color}`}></div>
                      <h3 className="font-semibold text-foreground text-xs sm:text-sm">{stage.label}</h3>
                    </div>
                    <Badge variant="outline" className="bg-background text-xs">{stageLeads.length}</Badge>
                  </div>
                </div>
                
                <div className="space-y-3 sm:space-y-4">
                  {stageLeads.map(lead => (
                    lead.quickscan_data ? (
                      <QuickscanLeadCard
                        key={lead.id}
                        lead={lead}
                        onDragStart={(e) => handleDragStart(e, lead.id)}
                        onClick={() => selectionMode ? toggleSelection(lead.id) : setSelectedLead(lead.id)}
                        onExport={() => {}}
                        selectionMode={selectionMode}
                        selected={selectedLeads.has(lead.id)}
                        onToggleSelection={() => toggleSelection(lead.id)}
                      />
                    ) : (
                      <LeadCard
                        key={lead.id}
                        lead={lead}
                        onDragStart={(e) => handleDragStart(e, lead.id)}
                        onClick={() => selectionMode ? toggleSelection(lead.id) : setSelectedLead(lead.id)}
                        selectionMode={selectionMode}
                        selected={selectedLeads.has(lead.id)}
                        onToggleSelection={() => toggleSelection(lead.id)}
                      />
                    )
                  ))}
                  {stageLeads.length === 0 && (
                    <div className="text-center py-6 sm:py-8 text-muted-foreground">
                      <div className="text-xs sm:text-sm">Geen leads</div>
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Bulk Actions Toolbar */}
      {selectionMode && selectedLeads.size > 0 && (
        <BulkActionsToolbar
          selectedCount={selectedLeads.size}
          onClearSelection={clearSelection}
          onBulkStageUpdate={handleBulkStageUpdate}
          onBulkOwnerUpdate={handleBulkOwnerUpdate}
          onBulkDelete={handleBulkDelete}
          stages={LEAD_STAGES}
        />
      )}

      {/* Lead Detail Modal */}
      {selectedLeadData && (
        <LeadDetailModal
          lead={selectedLeadData}
          onClose={() => setSelectedLead(null)}
          onLeadDeleted={() => {
            setSelectedLead(null);
            refetch(); // Auto-refresh after deletion
          }}
          onLeadUpdated={refetch}
        />
      )}

      {/* New Lead Modal */}
      {showNewLeadModal && (
        <NewLeadModal
          onClose={() => setShowNewLeadModal(false)}
        />
      )}
    </div>
  );
};

export default CRMLeads;
